import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { getLoadedEmojiIndex, type EmojiMatch } from "@/lib/emoji-index";
import { SuggestionList, suggestionKeyDown } from "./suggestion-list";

export interface EmojiSuggestionListProps {
  items: EmojiMatch[];
  command: (item: EmojiMatch) => void;
  /** Positions the list under the field; the editors' tippy popups position it themselves. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  onDismiss?: () => void;
  testId?: string;
}

/** Imperative handle the suggestion plugin calls on each editor keydown. */
export interface EmojiSuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const EmojiSuggestionList = forwardRef<EmojiSuggestionListRef, EmojiSuggestionListProps>(
  function EmojiSuggestionList({ items, command, anchorRef, onDismiss, testId }, ref) {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
      setActiveIndex(0);
    }, [items]);

    const select = (index: number) => {
      const item = items[index];
      if (item) command(item);
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        // Still loading: hold Enter/Tab rather than let them submit or add a line.
        if (items.length === 0) return !getLoadedEmojiIndex() && (event.key === "Enter" || event.key === "Tab");
        return suggestionKeyDown(event.key, { count: items.length, activeIndex, setActiveIndex, select });
      }
    }));

    return (
      <SuggestionList
        label="Emoji"
        groups={[
          {
            items: items.map((item) => ({
              key: `${item.emoji}-${item.shortcode}`,
              icon: (
                <span className="w-5 shrink-0 text-center text-base leading-none" aria-hidden>
                  {item.emoji}
                </span>
              ),
              label: <span className="text-muted-foreground">:{item.shortcode}:</span>
            }))
          }
        ]}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveIndex}
        onSelect={select}
        empty={getLoadedEmojiIndex() ? "No matches" : "Loading…"}
        anchorRef={anchorRef}
        onDismiss={onDismiss}
        testId={testId}
      />
    );
  }
);

export default EmojiSuggestionList;
